import { computed, reactive, ref } from 'vue'
import { cloneDeep, get } from 'lodash'

import { CATEGORY_TYPE, SALES_PARTNER_TYPE } from '@/config/constants'
import { productAVMtypes } from '@/components/MedicalSearch/config/constants'
import { EVENT_BASKET, EVENT_PRODUCT } from '@/config/events'

import { isRequired, MissingParameterError } from '@/utils/CustomErrors'
import { DateTimeUtil } from '@/utils/DateTime'
import { ReactivityUtil } from '@/utils/Reactivity'
import events$ from '@/services/Events'
import config from '@/config'

import useBackend from '@/hooks/useBackend'
import useCategories from '@/hooks/useCategories'
import useFranchise from '@/hooks/useFranchise'
import useLogger from '@/hooks/useLogger'
import usePartner from '@/hooks/usePartner'
import useI18n from '@/hooks/useI18n'

import { filterSoftCloseProductsHaX, haXProducts } from '@/utils/HaX'
import {
  getSortedProductsFromGroup,
  getSortedProductsFromCategory,
  mapIdToProductId,
  sortProducts,
} from '@/utils/Product'

// Hooks
const { setup, doGet, doPost } = useBackend()
const { getCategory, getGroup } = useCategories()
const { formatCurrency } = useFranchise()
const { logInfo } = useLogger()
const { partner } = usePartner()

setup('/ovpv3/public', config.microserviceBaseUrl)

// Data
const loaded = ref(false)
const productData = reactive({
  categories: {},
  groups: {},
  products: {},
})

// Events
events$.on(EVENT_BASKET.UPDATED, ({ basket: __basket, originalBasket }) => {
  initProducts(__basket, originalBasket)
})

// Computed
const categories = computed(() => {
  return productData.categories
})

const groups = computed(() => {
  return productData.groups
})

const products = computed(() => {
  const data = cloneDeep(productData.products)

  Object.keys(productData.products).forEach(__productId => {
    mapIdToProductId(data, __productId)
  })

  return data
})

// Methods

/**
 *  calculateAllPricesForProduct
 *  @param {string} productId
 *  @param {Basket} payload
 *  @returns {PriceCombination[]}
 */
async function calculateAllPricesForProduct(productId, payload) {
  const priceCalculations = await doPost(`products/${productId}/calculations`, payload)
  return priceCalculations.data
}

/**
 *  composeOptions
 *  TODO: Move to product utils
 *  @param {ProductProduct} product
 *  @param {string} groupId
 *  @param {string} categoryId
 *  @returns {string}
 */
function composeOptions(product, groupId, categoryId) {
  const { t } = useI18n()

  const optionTitles = getOptionTitles(product.productId, groupId, categoryId)
  const price = product.prices.find(p => p.selected)

  const options = []
  if (hasAccident(product)) {
    options.push(t(`product.accident.${price.toggle ? 'on' : 'off'}`))
  }

  // @note for now, only for the vital-product (m)
  if (hasVitalFranchise(product)) {
    options.push(
      `${optionTitles.amValue.title} ${formatCurrency(price.amValue, null, 0)}`,
      `${optionTitles.gfValue.title} ${formatCurrency(price.gfValue, null, 0)}`
    )
  }

  // @note do not use `hasFranchise` here, as that is used to check, if franchise-selection needs to be display
  if (price.value !== undefined) {
    const franchise = `${optionTitles.franchise.title} ${formatCurrency(price.value, null, 0)} `
    options.push(franchise)
  }

  if (hasDuration(product) && optionTitles.duration) {
    switch (price.duration.toString()) {
      case '1':
        options.push(t('product.contractDuration.single', { year: price.duration }))
        break

      case '3':
      case '5':
        options.push(t('product.contractDuration.multiple', { years: price.duration }))
        break

      case 'F':
        options.push(t('product.contractDuration.extended'))
        break
    }
  }

  return options.join(', ')
}

/**
 *  fetchProducts gets the productStructure from the ovpv3-ms api
 *  @param {Basket} __basket
 *  @returns {Promise<ProductStructure>}
 */
async function fetchProducts(__basket) {
  if (!__basket?.masterTreaty) throw new MissingParameterError('masterTreaty')

  const startDates = getAllContractStartDates(__basket)
  const payload = {
    collectiveChannel: __basket.collective?.channel,
    collectiveContractNumber: __basket.collective?.contractNumber,
    contractStartDates: Array.from(startDates),
    masterTreaty: __basket.masterTreaty,
  }

  const response = await doGet('products', payload)
  return response.data
}

/**
 * getAVMId
 * @todo move to different hooks (e.g. useDoctor)
 * @param {string} productId
 * @returns {}
 */
function getAVMId(productId) {
  return get(productAVMtypes, productId, '')
}

/**
 *  getCategoryFromProduct
 *  @param {string} productId
 *  @returns {ProductCategory}
 */
function getCategoryFromProduct(productId) {
  return categories.value[getCategoryIdFromProduct(productId)]
}

/**
 *  getCategoryIdFromProduct gets the categoryId from a product.
 *  If the passed product is an upgrade, the productId if it's parent will be used
 *  @param {string} productId
 *  @returns {string | undefined}
 */
function getCategoryIdFromProduct(productId = isRequired('productId')) {
  if (!productData?.categories) throw new MissingParameterError('categories')
  if (!productData?.groups) throw new MissingParameterError('groups')

  const { categories: rawCategories, groups: rawGroups } = productData

  if (!rawCategories) return undefined

  let __categoryId
  let __productId = productId

  if (isUpgrade(productId)) {
    __productId = getParentProduct(productId)
  }

  Object.keys(rawCategories).find(categoryId => {
    const _match = rawCategories[categoryId].groups.find(groupId => {
      return rawGroups[groupId].products.includes(__productId)
    })

    if (_match) {
      __categoryId = categoryId
    }
  })

  return __categoryId
}

/**
 *  getGroupFromProduct
 *  @param {string} productId
 *  @returns {ProductGroup}
 */
function getGroupFromProduct(productId) {
  return groups.value[getGroupIdFromProduct(productId)]
}

/**
 * getGroupIdFromProduct gets the groupId from a product.
 * If the passed product is an upgrade, the productId if it's parent will be used
 * @param {string} productId
 * @returns {string} groupId
 */
function getGroupIdFromProduct(productId = isRequired('productId')) {
  let __productId = productId

  if (isUpgrade(productId)) {
    __productId = getParentProduct(productId)
  }

  return Object.keys(productData.groups).find(groupId => productData.groups[groupId].products.includes(__productId))
}

/**
 * getOptionTitles
 * @param {string} productId
 * @param {string} groupId
 * @param {string} categoryId
 * @returns {}
 */
function getOptionTitles(productId, groupId, categoryId) {
  const { t, te } = useI18n()
  const data = { franchise: {}, accident: {} }

  if (te(`content.categories.${categoryId}.accident`)) {
    data.accident = t(`content.categories.${categoryId}.accident`)
  } else if (te(`content.groups.${groupId}.accident`)) {
    data.accident = t(`content.groups.${groupId}.accident`)
  } else if (te(`content.products.${productId}.accident`)) {
    data.accident = t(`content.products.${productId}.accident`)
  }

  if (te(`content.categories.${categoryId}.duration`)) {
    data.duration = t(`content.categories.${categoryId}.duration`)
  } else if (te(`content.groups.${groupId}.duration`)) {
    data.duration = t(`content.groups.${groupId}.duration`)
  } else if (te(`content.products.${productId}.duration`)) {
    data.duration = t(`content.products.${productId}.duration`)
  }

  if (te(`content.categories.${categoryId}.franchise`)) {
    data.franchise = t(`content.categories.${categoryId}.franchise`)
  } else if (te(`content.groups.${groupId}.franchise`)) {
    data.franchise = t(`content.groups.${groupId}.franchise`)
  } else if (te(`content.products.${productId}.franchise`)) {
    data.franchise = t(`content.products.${productId}.franchise`)
  }

  if (te(`content.categories.${categoryId}.amValue`)) {
    data.amValue = t(`content.categories.${categoryId}.amValue`)
  } else if (te(`content.groups.${groupId}.amValue`)) {
    data.amValue = t(`content.groups.${groupId}.amValue`)
  } else if (te(`content.products.${productId}.amValue`)) {
    data.amValue = t(`content.products.${productId}.amValue`)
  }

  if (te(`content.categories.${categoryId}.gfValue`)) {
    data.gfValue = t(`content.categories.${categoryId}.gfValue`)
  } else if (te(`content.groups.${groupId}.gfValue`)) {
    data.gfValue = t(`content.groups.${groupId}.gfValue`)
  } else if (te(`content.products.${productId}.gfValue`)) {
    data.gfValue = t(`content.products.${productId}.gfValue`)
  }

  return data
}

/**
 *  hasAccident returns true if the passed product has the accident option
 *  @param {object} product
 *  @returns {boolean}
 */
function hasAccident(product) {
  // eslint-disable-next-line no-prototype-builtins
  return product.prices.find(p => p.selected)?.hasOwnProperty('toggle')
}

/**
 *  hasAccidentToggle returns indicates if the accident toggle should be displayed for a product
 *  @param {{categoryId: string, dateOfBirth: string, kvgContractStartDate: string, product: object}}
 *  @returns {boolean}
 */
function hasAccidentToggle({ categoryId, dateOfBirth, kvgContractStartDate, product }) {
  if (product && !hasAccident(product)) return false
  if (categoryId !== CATEGORY_TYPE.KVG) return true

  // FLEX-1303 Age at the end of year of contract start date >= 16 -> accident always true for KVG and toggle hidden
  const ageEndOfContractStartYear = DateTimeUtil.getAgeEndOfContractStartYear(dateOfBirth, kvgContractStartDate)
  return ageEndOfContractStartYear >= 16 && ageEndOfContractStartYear <= 65
}

/**
 *  hasDuration returns true if a product has a duration option
 *  @param {object} product
 *  @returns {boolean}
 */
function hasDuration(product) {
  const isDurationVisibleForPartners = [
    SALES_PARTNER_TYPE.PRIVATE_INSURER,
    SALES_PARTNER_TYPE.BROKER,
    SALES_PARTNER_TYPE.MOBILE,
  ].includes(partner.value.base?.salesPartnerType)

  // eslint-disable-next-line no-prototype-builtins
  const hasProp = product.prices.find(p => p.selected)?.hasOwnProperty('duration')

  return (isDurationVisibleForPartners || product.productId === 'SBVB01Produkt') && hasProp
}

/**
 * hasFranchise returns true if a product has a franchise option
 * @NOTE Is used to know, if the franchise-selection needs to be displayed.
 * If you need to check, if there's a value, check for value on the selected options on a product
 * @param {object} product
 * @returns {boolean}
 */
function hasFranchise(product) {
  const values = []
  product
  product.prices.forEach(p => {
    values.push(p.value)
  })
  return [...new Set(values)].length > 1
}

/**
 *  hasOptions returns true if a product has any option at all
 *  @param {object} product
 *  @returns {boolean}
 */
function hasOptions(product) {
  return hasAccident(product) || hasFranchise(product) || hasDuration(product) || hasVitalFranchise(product)
}

/**
 *  hasVitalFranchise returns true if a product has a vital franchise
 *  @param {object} product
 *  @returns {boolean}
 */
function hasVitalFranchise(product) {
  return product.prices.find(p => !!p.amValue && !!p.gfValue)
}

/**
 *  isProductReloadNecessary returns true if either a contract start date, a collective contract number or the language has changed
 *  @param {Basket} newBasket
 *  @param {Basket} oldBasket
 *  @returns {boolean}
 */
function isProductReloadNecessary(newBasket, oldBasket) {
  return (
    newBasket?.collective?.contractNumber !== oldBasket.collective?.contractNumber ||
    hasContractStartDateChanged(newBasket, oldBasket) ||
    newBasket?.language !== oldBasket.language
  )
}

/**
 *  isUpgrade returns the product if it is an upgrade
 *  @param {string} productId
 *  @returns {ProductProduct | undefined}
 */
function isUpgrade(productId = isRequired('productId')) {
  return Object.keys(products.value).find(_productId => products.value[_productId].upgrades.includes(productId))
}

// PRIVATE METHODS

/**
 *  getAllContractStartDates returns a set with all contract startdates (kvg and vvg) for all persons
 *  @param {Basket} __basket
 *  @returns {}
 */
function getAllContractStartDates(__basket) {
  const contractStartDates = new Set()
  if (__basket.persons) {
    __basket.persons.forEach(p => {
      contractStartDates.add(p.kvgContractStartDate)
      contractStartDates.add(p.vvgContractStartDate)
    })
  }
  return contractStartDates
}

/**
 *  getParentProduct
 *  @param {string} productId
 *  @returns {ProductProduct | undefined}
 */
function getParentProduct(productId = isRequired('productId')) {
  return Object.keys(productData.products).find(__productId => {
    return products.value[__productId].upgrades.find(__upgradeId => __upgradeId === productId)
  })
}

/**
 *  hasContractStartDateChanged returns true if the provided baskets don't contain the same contract startdates
 *  @param {Basket} newBasket
 *  @param {Basket} oldBasket
 *  @returns {boolean}
 */
function hasContractStartDateChanged(newBasket, oldBasket) {
  const newContractStartDates = getAllContractStartDates(newBasket)
  const oldContractStartDates = getAllContractStartDates(oldBasket)

  if (newContractStartDates.size !== oldContractStartDates.size) {
    return true
  }

  for (const startDate of newContractStartDates) {
    if (!oldContractStartDates.has(startDate)) {
      return true
    }
  }

  return false
}

/**
 * initProducts loads productStructure, if products need to be loaded
 * caveat: also get's called on initial local basket-initialization, but cancels, because masterTreaty is missing
 *
 * @param {Basket} __basket
 * @param {Basket} originalBasket
 */
async function initProducts(__basket, originalBasket) {
  if (__basket?.persons?.length === 0) return

  const __reloadNecessary = !!isProductReloadNecessary(__basket, originalBasket)

  if ((__reloadNecessary || loaded.value === false) && __basket.masterTreaty) {
    let structure = await fetchProducts(__basket)

    if (structure) {
      loaded.value = true
      setProducts(structure)
    }
  }
}

/**
 *  setProducts takes a productStructure and stores it in the local data
 *  @param {ProductStructure} payload
 */
function setProducts(payload) {
  /*
   *  HaX for soft close:
   *  We filter out soft closed products (classic, family, jump)
   *  and store these filtered products in the original productData
   *  (as we no longer want to show these products to the users)
   *  But as we need the original (non-filtered) products for the
   *  existing customers to eventually replace the newly-selected vital
   *  product with the existing soft closed product, we store them
   *  in the haXProducts
   */
  const __payload = filterSoftCloseProductsHaX(ReactivityUtil.clone(payload))
  ReactivityUtil.reAssign(productData, __payload)
  ReactivityUtil.reAssign(haXProducts, payload)

  events$.emit(EVENT_PRODUCT.STRUCTURE_SET, __payload)
  logInfo(['%cORIGINAL PRODUCTS SET', 'color: green', payload])
  logInfo(['%cFILTERED PRODUCTS SET', 'color: green', __payload])
}

// CORE
function getReplaceableProducts(productId) {
  if (isUpgrade(productId)) return []

  let replaceableProducts = []
  const categoryId = getCategoryIdFromProduct(productId)
  // products could not have been loaded yet, so we need to check for it
  if (!categoryId) return []
  const category = getCategory(categoryId)
  const groupId = getGroupIdFromProduct(productId)
  const group = getGroup(groupId)

  const reducer = (acc, value) => {
    acc.push(value.productId)
    return acc
  }

  const getProductsFromCategory = sortProducts(
    getSortedProductsFromCategory({
      categories: categories.value,
      groups: groups.value,
      products: products.value,
      categoryId,
    }),
    products.value
  )
  const getProductsFromGroup = sortProducts(
    getSortedProductsFromGroup(groups.value, products.value, groupId),
    products.value
  )

  if (category?.exclusive) {
    replaceableProducts = getProductsFromCategory.reduce(reducer, [])
  } else if (group?.exclusive) {
    replaceableProducts = getProductsFromGroup.reduce(reducer, [])
  }

  return replaceableProducts
}

// Expose
export default function useProduct() {
  return {
    // COMPUTED
    categories,
    groups,
    products,

    // METHODS
    calculateAllPricesForProduct,
    composeOptions,
    fetchProducts,
    getAVMId,
    getCategoryFromProduct,
    getCategoryIdFromProduct,
    getGroupFromProduct,
    getGroupIdFromProduct,
    getOptionTitles,
    hasAccident,
    hasAccidentToggle,
    hasDuration,
    hasFranchise,
    hasOptions,
    hasVitalFranchise,
    isProductReloadNecessary,
    isUpgrade,

    getReplaceableProducts,
  }
}
